import type { CreateCollectionRequest, EditCollectionRequest } from '@kimuno/api-sdk';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Dialog, DialogContent, DialogTitle, IconButton, Stack } from '@mui/material';
import React, { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { customSizes, spaces } from '../../assets/styles/themes/tokens';
import type { CollectionFormModalContextState } from '../../state/CollectionFormModalProvider';
import DialogActions from '../common/DialogActions';
import {
	CollectionDescription,
	CollectionSchoolStages,
	CollectionSubjects,
	CollectionTitle,
	CollectionVisibility,
	CustomColorSelection,
} from './components';

interface CreateCollectionDialogProps {
	contextData: Pick<
		CollectionFormModalContextState,
		'isOpen' | 'hide' | 'save' | 'data' | 'collectionId' | 'canPublish'
	>;
}

const CreateCollectionDialog: React.FC<CreateCollectionDialogProps> = ({ contextData }) => {
	const { t } = useTranslation();
	const { isOpen, hide, save, data, collectionId, canPublish } = contextData;

	const formValidator = yup.object<any>({
		title: yup.string().required(t('This field is required')),
		description: yup.string(),
		subjects: yup.array().of(yup.string()),
		schoolStage: yup.string(),
		color: yup.string(),
		isPublic: yup.boolean(),
	});

	const { errors, handleSubmit, register, control } = useForm<any>({
		mode: 'onBlur',
		validationSchema: formValidator,
	});

	const handleOnSubmit = handleSubmit(
		useCallback(
			async (formData: CreateCollectionRequest | EditCollectionRequest) => {
				const payload = {
					title: formData.title,
					description: formData.description,
					subjects: formData.subjects,
					schoolStage: formData.schoolStage,
					color: formData.color,
					// Omitted entirely for users without the permission, so saving can never
					// change a collection's visibility from a form that never offered it.
					...(canPublish ? { isPublic: !!formData.isPublic } : {}),
				};

				if (collectionId) {
					return save(collectionId, payload as EditCollectionRequest);
				}
				return save(undefined, payload as CreateCollectionRequest);
			},
			[save, collectionId, canPublish]
		)
	);

	const DialogTitleSection = () => {
		return (
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					padding: spaces.lg,
				}}
			>
				<DialogTitle sx={{ padding: 0 }} variant="h5">
					{t('new collection')}
				</DialogTitle>
				<Box>
					<IconButton
						color="default"
						size="small"
						onClick={hide}
						data-testid="close-button"
						aria-label={t('Close')}
					>
						<CloseIcon />
					</IconButton>
				</Box>
			</Box>
		);
	};

	return (
		<Dialog
			fullWidth
			aria-labelledby="create-collection-dialog"
			data-testid="create-collection-dialog"
			open={isOpen}
			onClose={hide}
			maxWidth={'lg'}
			sx={{
				'& .MuiDialog-paper': {
					width: customSizes.createCollectionDialog.width,
				},
			}}
		>
			<DialogTitleSection />
			<DialogContent
				sx={{
					padding: `${spaces.md} ${spaces.lg}`,
				}}
			>
				<Box component="form" data-testid="create-collection-dialog-form">
					<Stack direction="column" spacing={spaces.lg}>
						<CollectionTitle inputRef={register} error={errors.title} initialValue={data?.title} />
						<CollectionDescription
							inputRef={register}
							error={errors.description}
							initialValue={data?.description}
						/>
						<CollectionSubjects control={control} initialValue={data?.subjects} />
						<CollectionSchoolStages control={control} initialValue={data?.schoolStage} />
						<CustomColorSelection control={control} initialValue={data?.color} />
						{canPublish && <CollectionVisibility inputRef={register} initialValue={data?.isPublic} />}
					</Stack>
				</Box>
			</DialogContent>
			<DialogActions
				primaryButton={{
					text: t('Save'),
					onClick: handleOnSubmit,
					type: 'submit',
				}}
				secondaryButton={{
					text: t('Cancel'),
					onClick: hide,
				}}
			/>
		</Dialog>
	);
};

export default CreateCollectionDialog;
